import { format, parse } from 'date-fns';

import {
    GenericValidation,
    NodePayload,
    ValidationInfo,
} from '../../features/genericBlade/genericBladeTypes';

export const YEAR_MONTH_DAY_FORMAT = 'yyyy-MM-dd';
export const MONTH_YEAR_FORMAT = 'MMMM yyyy';
export const DAY_MONTH_YEAR_FORMAT = 'dd MMMM yyyy';
export const DD_MM_YEAR_FORMAT = 'dd/MM/yyyy';
export const YEAR_MONTH_DAY_TIME_FORMAT = 'yyyy-MM-dd\'T\'HH:mm:ss';

export const WEEKS_PER_YEAR = 52;
export const FORTNIGHTS_PER_YEAR = 26;
export const MONTHS_PER_YEAR = 12;

/**
 *
 * @param dateString the date string to parse
 * @param dateFormat the input date format, e.x. 'yyyy-mm-dd', 'MM-yyyy'
 * @param outputFormat the output date format, e.x. 'yyyy-mm-dd, 'MM-yyyy'
 */
export const formatDate = (
    dateString: string,
    dateFormat: string,
    outputFormat: string,
): string => format(parse(dateString, dateFormat, new Date()), outputFormat);

export const isISODate = (dateString: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|([+-]\d{2}:\d{2}))?$/.test(dateString);

export const isLocalDateFormat = (dateString: string) => /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/.test(dateString);

export const isServerDateFormat = (dateString: string) => (
    /^(?:\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?|\d{4}-\d{2}-\d{2})$/.test(dateString)
);

export const convertToISODate = (dateString: string) => {
    const parsedDate = parse(dateString, DD_MM_YEAR_FORMAT, new Date());
    
    return parsedDate.toISOString();
};

export const parseDateString = (dateString: string) => {
    if (isLocalDateFormat(dateString)) {
        return parse(dateString, DD_MM_YEAR_FORMAT, new Date());
    } else if (isServerDateFormat(dateString)) {
        return new Date(dateString);
    }

    return new Date('Invalid Date');
};

export const convertToServerDate = (dateString: string) => {
    const date = parseDateString(dateString);

    if (date.toString() !== 'Invalid Date') {
        return format(date, YEAR_MONTH_DAY_FORMAT);
    } 
    
    return dateString;
};

export const capitalize = (str: string) => str[0].toUpperCase() + str.toLowerCase().slice(1);

export const toTitleCase = (str: string) => {
    if (!str || str.length == 0) {
        return '';
    }

    return str.trim().split(' ').map(text => text.trim().split('-').map(t => t ? ''.concat(t[0]?.toUpperCase()).concat(t.substring(1)?.toLowerCase()) : '').join('-')).join(' ');
};

export const parseContent = (content: string, params: string[]): string => (
    content.replace(/{(\d+)}/g, (match: string, index: number) => (
        typeof params[index] !== 'undefined' ? params[index] : match
    ))
);

export const arraysEqual = (array1: string[], array2: string[]): boolean => (
    array1.length === array2.length && array1.every(element => array2.includes(element))
);

export const getValidationMessage = (
    validations: GenericValidation,
    value: NodePayload,
): string => {
    if (!validations) {
        return '';
    }

    const {
        defaultErrorMessage,
        negativeValueMessage,
        invalidAmountMessage,
        baseValue,
        baseValueMessage,
        minValue,
        minValueMessage,
        maxValue,
        maxValueMessage,
    } = validations;

    const parsedValue =  typeof value === 'string' ? parseFloat(value) : value as number ;

    if (defaultErrorMessage && !value && parsedValue !== 0) {
        return defaultErrorMessage;
    }
    if ((invalidAmountMessage || negativeValueMessage || minValueMessage || baseValueMessage || maxValueMessage) && isNaN(parsedValue)) {
        return invalidAmountMessage ?? 'Invalid';
    }
    if (negativeValueMessage && parsedValue < 0) {
        return negativeValueMessage;
    }
    if ((baseValue && parsedValue === (typeof baseValue === 'string' ? parseFloat(baseValue) : baseValue))) {
        return baseValueMessage ?? 'Confirm amount: ' + baseValue;
    }
    if (minValue && parsedValue < (typeof minValue === 'string' ? parseFloat(minValue) : minValue)) {
        return minValueMessage ?? 'Entered amount is low: ' + minValue;
    }
    if (maxValue && parsedValue > (typeof maxValue === 'string' ? parseFloat(maxValue) : maxValue)) {
        return maxValueMessage ?? 'Entered amount is high: ' + maxValue;
    }

    return '';
};

export const getSoftValidationMessage = (
    validations: ValidationInfo | undefined,
    key: string,
    value: NodePayload | null,
): string => {
    if (!validations?.softValidations?.[key]) {
        return '';
    }

    const parsedValue = typeof value === 'string' ? parseFloat(value) : value as number;

    if(validations?.hardValidations?.[key] && getValidationMessage(validations.hardValidations[key], parsedValue)) {
        return '';
    }

    return getValidationMessage(validations.softValidations[key], parsedValue);
};

export const getIncomeTypeId = (value: string) => {
    const incomeId = value.split(':')[0];

    return incomeId.split('-')[0];
};

export const getAssetLiabilityTypeId = (value: string) => (value.includes(':') ? value.split(':')[1] : '');

export const getRepaymentAmountByFrequency = (repaymentPerWeek: number, frequency: string):number => {
    switch (frequency) {
    case 'Weekly':
        return repaymentPerWeek;
    case 'Fortnightly':
        return repaymentPerWeek * WEEKS_PER_YEAR / FORTNIGHTS_PER_YEAR;
    case 'Monthly':
        return repaymentPerWeek * WEEKS_PER_YEAR / MONTHS_PER_YEAR;
    }
    
    return repaymentPerWeek;
};
